import React, { useState, useCallback, useRef, useMemo } from 'react';
import { apiUrl } from '../services/api';
import { AIRLINES } from '../utils/airlines';

// ---------------------------------------------------------------------------
// Match parsed flights against existing trip activities.
// Returns each flight annotated with _status ('new' | 'update') and
// _existingActivityId (for updates).
// ---------------------------------------------------------------------------
function classifyFlights(parsedFlights, existingActivities, flightTypeId) {
  const flightActivities = (existingActivities || []).filter(
    a => String(a.type_id) === String(flightTypeId)
  );

  return parsedFlights.map(flight => {
    const firstLeg = flight.legs?.[0];
    // Can only dedup when the first leg has a flight number and depart date
    if (!firstLeg?.flight_number || !firstLeg?.depart_date) {
      return { ...flight, _status: 'new', _existingActivityId: null };
    }

    const match = flightActivities.find(a =>
      (a.legs || []).some(eLeg =>
        eLeg.flight_number === firstLeg.flight_number &&
        // depart_local_date is returned by the API; fall back to slicing the UTC string
        (eLeg.depart_local_date || eLeg.depart_time?.slice(0, 10)) === firstLeg.depart_date
      )
    );

    if (!match) return { ...flight, _status: 'new', _existingActivityId: null };
    return { ...flight, _status: 'update', _existingActivityId: String(match.activity_id), _existingActivity: match };
  });
}

// Human-readable time display for a leg (HH:MM)
function fmtTime(t) {
  if (!t) return '—';
  // Could be "17:40" or "2026-08-06T17:40:00+00:00"; show HH:MM portion
  const m = t.match(/(\d{2}:\d{2})/);
  return m ? m[1] : t;
}

// Status badge shown on each flight card
function StatusBadge({ status }) {
  if (status === 'skip') {
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#333', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Skip
      </span>
    );
  }
  if (status === 'update') {
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(255,160,0,0.15)', color: '#ffa000', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Schedule change · will update
      </span>
    );
  }
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(72,199,142,0.15)', color: '#48c78e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      New flight
    </span>
  );
}


// Best-effort: pick the trip destination matching a flight's arrival city (then
// departure city) so imported flights land on a destination automatically and
// aren't left invisible. Returns '' when nothing matches (user can still pick).
function matchDestinationId(flight, destinations) {
  const dests = Array.isArray(destinations) ? destinations : [];
  if (!dests.length) return '';
  const norm = s => String(s || '').toLowerCase();
  const legs = Array.isArray(flight.legs) ? flight.legs : [];
  const findIn = (cityText) => {
    if (!cityText) return null;
    return dests.find(d => {
      const name = norm(d.destination_name).split(',')[0].trim();
      return name && cityText.includes(name);
    });
  };
  const match = findIn(norm(legs[legs.length - 1]?.to_text)) || findIn(norm(legs[0]?.from_text));
  return match ? String(match.destination_id) : '';
}

export default function FlightImportModal({ onClose, onImport, destinations, activityTypes, activityCategories, existingActivities }) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);   // annotated with _status + _destination_id
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const fileRef = useRef(null);

  const flightTypeId = useMemo(() => {
    const travelCat = (activityCategories || []).find(
      c => (c.category_name || '').toLowerCase() === 'travel'
    );
    if (!travelCat) return null;
    const flightType = (activityTypes || []).find(
      t => String(t.category_id) === String(travelCat.category_id) &&
           (t.type_name || '').toLowerCase() === 'flight'
    );
    return flightType?.type_id || null;
  }, [activityCategories, activityTypes]);

  const handleParse = useCallback(async () => {
    setError('');
    setEditing(null);

    const file = fileRef.current?.files?.[0];
    if (!text.trim() && !file) {
      setError('Paste your flight confirmation, or upload a PDF or .ics file.');
      return;
    }

    setLoading(true);
    try {
      let res;
      if (file) {
        const form = new FormData();
        form.append('file', file);
        if (text.trim()) form.append('text', text.trim());
        res = await fetch(apiUrl('/api/flights/parse'), { method: 'POST', body: form });
      } else {
        res = await fetch(apiUrl('/api/flights/parse'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim() }),
        });
      }

      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to parse flights'); return; }
      if (!data.flights?.length) {
        setError('No flights detected. Try including more detail (airline, airports, dates, times).');
        return;
      }

      const parsed = data.flights.map(f => ({
        title: f.title || '',
        airline_name: f.airline_name || '',
        confirmation_number: f.confirmation_number || '',
        legs: (f.legs || []).map(l => ({
          ...l,
          airline_name: l.airline_name || f.airline_name || '',
          from_text: l.from_text || l.from_airport || '',
          to_text: l.to_text || l.to_airport || '',
          depart_date: l.depart_date || '',
          depart_time: l.depart_time || '',
          arrive_date: l.arrive_date || '',
          arrive_time: l.arrive_time || '',
          flight_number: l.flight_number || '',
        })),
      }));

      const classified = classifyFlights(parsed, existingActivities, flightTypeId);
      // Existing activities keep their destination; new flights default to trip transit (no destination)
      setEditing(classified.map(f => ({
        ...f,
        _destination_id: f._existingActivity?.destination_id ?? matchDestinationId(f, destinations),
      })));
    } catch {
      setError('Network error – could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [text, existingActivities, flightTypeId, destinations]);

  const handleFileChange = useCallback((e) => {
    setFileName(e.target.files?.[0]?.name || '');
  }, []);

  const updateFlight = useCallback((fIdx, field, value) => {
    setEditing(prev => prev.map((f, i) => i === fIdx ? { ...f, [field]: value } : f));
  }, []);

  const updateLeg = useCallback((fIdx, lIdx, field, value) => {
    setEditing(prev =>
      prev.map((f, fi) =>
        fi === fIdx
          ? { ...f, legs: f.legs.map((l, li) => li === lIdx ? { ...l, [field]: value } : l) }
          : f
      )
    );
  }, []);

  const toggleSkip = useCallback((fIdx) => {
    setEditing(prev => prev.map((f, i) => {
      if (i !== fIdx) return f;
      if (f._status === 'skip') {
        // Restore to original classification
        return { ...f, _status: f._originalStatus || 'new' };
      }
      return { ...f, _originalStatus: f._status, _status: 'skip' };
    }));
  }, []);

  const handleImport = useCallback(() => {
    if (!editing?.length) return;

    const flights = editing
      .filter(f => f._status !== 'skip')
      .map(f => {
        const legs = f.legs.map((l, i) => ({
          leg_index: i,
          type_id: flightTypeId,
          airline: l.airline_name || f.airline_name || null,
          flight_number: l.flight_number || null,
          from_address: l.from_text || null,
          to_address: l.to_text || null,
          from_airport: l.from_airport || null,
          to_airport: l.to_airport || null,
          from_city: null, to_city: null,
          from_lat: null, from_lon: null, to_lat: null, to_lon: null,
          depart_time: l.depart_date && l.depart_time ? `${l.depart_date}T${l.depart_time}` : null,
          arrive_time: (l.arrive_date || l.depart_date) && l.arrive_time
            ? `${l.arrive_date || l.depart_date}T${l.arrive_time}` : null,
        }));

        const description = f.confirmation_number ? `**Confirmation:** ${f.confirmation_number}` : '';

        return {
          _status: f._status,
          _existingActivityId: f._existingActivityId || null,
          _existingTitle: f._existingActivity?.title || null,
          _existingDescription: f._existingActivity?.description || null,
          title: f.title || 'Imported Flight',
          description,
          type_id: flightTypeId,
          destination_id: f._destination_id || null,
          airline: f.airline_name || null,
          legs,
          start_time: legs[0]?.depart_time || null,
          end_time: legs[legs.length - 1]?.arrive_time || null,
        };
      });

    onImport(flights);
  }, [editing, flightTypeId, onImport]);

  const hasResults = !!editing?.length;

  // Summary counts for the import button
  const counts = useMemo(() => {
    if (!editing) return null;
    const active = editing.filter(f => f._status !== 'skip');
    const newCount = active.filter(f => f._status === 'new').length;
    const updateCount = active.filter(f => f._status === 'update').length;
    return { total: active.length, newCount, updateCount };
  }, [editing]);

  const importLabel = (() => {
    if (!counts) return 'Import';
    if (counts.total === 0) return 'Nothing to import';
    const parts = [];
    if (counts.newCount) parts.push(`${counts.newCount} new`);
    if (counts.updateCount) parts.push(`${counts.updateCount} update`);
    return `Import (${parts.join(', ')})`;
  })();

  return (
    <div className="tp-modal-backdrop" onClick={onClose}>
      <div
        style={{
          background: 'rgba(18, 18, 18, 0.98)',
          border: '1px solid var(--tp-border)',
          borderRadius: 16,
          padding: 24,
          maxWidth: hasResults ? 800 : 560,
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
          transition: 'max-width 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Import Flights</h3>

        {!hasResults ? (
          <>
            <div className="tp-field" style={{ marginBottom: 12 }}>
              <span className="tp-label">Paste confirmation email or flight details</span>
              <textarea
                rows={8}
                placeholder={
                  'Paste your flight confirmation here…\n\n' +
                  'Example:\n' +
                  'American Airlines\n' +
                  'Confirmation: ABCDEF\n' +
                  'Flight AA 1234\n' +
                  'DFW → BCN\n' +
                  'August 6, 2026\n' +
                  'Departs: 5:40 PM  Arrives: 10:10 AM'
                }
                value={text}
                onChange={e => setText(e.target.value)}
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5 }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <button
                type="button"
                className="tp-btn--ghost"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                onClick={() => fileRef.current?.click()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                {fileName || 'Upload PDF or .ics'}
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.ics,text/calendar" style={{ display: 'none' }} onChange={handleFileChange} />
              {fileName && (
                <button type="button" className="tp-btn--ghost" style={{ fontSize: 12, opacity: 0.7 }}
                  onClick={() => { setFileName(''); if (fileRef.current) fileRef.current.value = ''; }}>
                  Clear
                </button>
              )}
            </div>

            {error && <div style={{ color: 'var(--tp-danger, #e55)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="tp-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="button" onClick={handleParse} disabled={loading}>
                {loading ? 'Parsing…' : 'Parse Flights'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 16px' }}>
              Flights default to <strong>Trip transit</strong> — they appear in the timeline between destinations. Set a destination only for local travel within a city (e.g. airport transfer).
            </p>

            {editing.map((flight, fIdx) => (
              <div
                key={fIdx}
                style={{
                  border: `1px solid ${flight._status === 'skip' ? '#333' : flight._status === 'update' ? 'rgba(255,160,0,0.3)' : 'var(--tp-border)'}`,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  opacity: flight._status === 'skip' ? 0.45 : 1,
                  transition: 'opacity 0.15s, border-color 0.15s',
                }}
              >
                {/* Header: status badge + destination + skip toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <StatusBadge status={flight._status} />
                  <select
                    value={flight._destination_id || ''}
                    onChange={e => updateFlight(fIdx, '_destination_id', e.target.value)}
                    style={{ flex: 1, minWidth: 140, fontSize: 13 }}
                    disabled={flight._status === 'skip'}
                  >
                    <option value="">Trip transit</option>
                    {(destinations || []).map(d => (
                      <option key={d.destination_id} value={String(d.destination_id)}>{d.destination_name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="tp-btn--ghost"
                    style={{ fontSize: 12, padding: '2px 10px', flexShrink: 0 }}
                    onClick={() => toggleSkip(fIdx)}
                  >
                    {flight._status === 'skip' ? 'Restore' : 'Skip'}
                  </button>
                </div>

                {/* Show before/after times for schedule changes */}
                {flight._status === 'update' && flight._existingActivity?.legs?.length > 0 && (
                  <div style={{ fontSize: 12, color: '#ffa000', background: 'rgba(255,160,0,0.07)', borderRadius: 6, padding: '6px 10px', marginBottom: 12 }}>
                    {flight._existingActivity.legs.map((eLeg, li) => {
                      const nLeg = flight.legs[li];
                      if (!nLeg) return null;
                      const oldDep = fmtTime(eLeg.depart_time);
                      const newDep = nLeg.depart_time || '—';
                      const oldArr = fmtTime(eLeg.arrive_time);
                      const newArr = nLeg.arrive_time || '—';
                      const depChanged = oldDep !== newDep;
                      const arrChanged = oldArr !== newArr;
                      if (!depChanged && !arrChanged) return null;
                      return (
                        <div key={li} style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {depChanged && <span>Departs: <s style={{ opacity: 0.5 }}>{oldDep}</s> → <strong>{newDep}</strong></span>}
                          {arrChanged && <span>Arrives: <s style={{ opacity: 0.5 }}>{oldArr}</s> → <strong>{newArr}</strong></span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div className="tp-field">
                    <span className="tp-label">Title</span>
                    <input type="text" value={flight.title} onChange={e => updateFlight(fIdx, 'title', e.target.value)} />
                  </div>
                  <div className="tp-field">
                    <span className="tp-label">Airline</span>
                    <select value={flight.airline_name} onChange={e => updateFlight(fIdx, 'airline_name', e.target.value)}>
                      <option value="">Select airline</option>
                      {AIRLINES.map(a => (
                        <option key={a.code} value={a.name}>{a.code ? `${a.code} – ${a.name}` : a.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="tp-field" style={{ marginBottom: 12 }}>
                  <span className="tp-label">Confirmation Number</span>
                  <input type="text" placeholder="e.g. ABCDEF" value={flight.confirmation_number}
                    onChange={e => updateFlight(fIdx, 'confirmation_number', e.target.value)} />
                </div>

                {flight.legs.map((leg, lIdx) => (
                  <div key={lIdx} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    {flight.legs.length > 1 && (
                      <span style={{ fontSize: 12, opacity: 0.5, marginBottom: 6, display: 'block' }}>Leg {lIdx + 1}</span>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 8 }}>
                      <div className="tp-field">
                        <span className="tp-label">Airline</span>
                        <input type="text" value={leg.airline_name || ''} onChange={e => updateLeg(fIdx, lIdx, 'airline_name', e.target.value)} />
                      </div>
                      <div className="tp-field">
                        <span className="tp-label">Flight #</span>
                        <input type="text" value={leg.flight_number || ''} onChange={e => updateLeg(fIdx, lIdx, 'flight_number', e.target.value)} placeholder="e.g. AA192" />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                      <div className="tp-field">
                        <span className="tp-label">From</span>
                        <input type="text" value={leg.from_text} onChange={e => updateLeg(fIdx, lIdx, 'from_text', e.target.value)} />
                      </div>
                      <div className="tp-field">
                        <span className="tp-label">To</span>
                        <input type="text" value={leg.to_text} onChange={e => updateLeg(fIdx, lIdx, 'to_text', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                      <div className="tp-field">
                        <span className="tp-label">Depart Date</span>
                        <input type="date" value={leg.depart_date} onChange={e => updateLeg(fIdx, lIdx, 'depart_date', e.target.value)} />
                      </div>
                      <div className="tp-field">
                        <span className="tp-label">Depart Time</span>
                        <input type="time" value={leg.depart_time} onChange={e => updateLeg(fIdx, lIdx, 'depart_time', e.target.value)} />
                      </div>
                      <div className="tp-field">
                        <span className="tp-label">Arrive Date</span>
                        <input type="date" value={leg.arrive_date} onChange={e => updateLeg(fIdx, lIdx, 'arrive_date', e.target.value)} />
                      </div>
                      <div className="tp-field">
                        <span className="tp-label">Arrive Time</span>
                        <input type="time" value={leg.arrive_time} onChange={e => updateLeg(fIdx, lIdx, 'arrive_time', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="tp-btn--ghost" onClick={() => { setEditing(null); setError(''); }}>Back</button>
              <button type="button" className="tp-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="button" onClick={handleImport} disabled={counts?.total === 0}>
                {importLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
